import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";
import { buildBvh } from "./icpRegister";
import { rigidIncrement, solveLinearSystem } from "./linalg";
import {
  IDENTITY_JAW,
  type JawTransform,
  jawTransformToMatrix,
  matrixToJawTransform,
} from "./meshMath";
import { getVertexAdjacency, getVertexAreas, triangleCorner } from "./meshPrep";

/** Distance thresholds in mm. */
export const CONTACT_MM = 0.05;
export const LIGHT_CONTACT_MM = 0.1;
export const NEAR_CONTACT_MM = 0.2;
/** Overlap below this is scan noise, not a clinical interference. */
export const PENETRATION_TOLERANCE_MM = 0.01;
export const MAX_CONTACT_ADJUSTMENTS = 5;
/** Colour ramp fades to grey beyond this clearance. */
export const COLOUR_CLEARANCE_CAP_MM = 1.5;
/** Closest-point queries stop searching beyond this distance (reported as clearance). */
export const DIST_QUERY_CAP_MM = 6;
/** Vertices whose interpolated gap is below this are always measured exactly. */
const REFINE_BELOW_MM = 0.6;
/** A sector counts as having teeth in occlusion when any surface is within this gap. */
const SECTOR_PRESENT_MM = 1.0;
/** Minimum contact area for a sector to count as contacting. */
const SECTOR_CONTACT_MIN_MM2 = 0.3;

export type ContactClass =
  | "interference"
  | "max_contact"
  | "light_contact"
  | "near_contact"
  | "clearance";

export const ARCH_SECTORS = [
  "Right posterior",
  "Right canine / premolar",
  "Anterior",
  "Left canine / premolar",
  "Left posterior",
] as const;

export interface QualityBreakdown {
  /** 1 = no penetration beyond 0.02 mm, 0 = ≥ 0.25 mm. */
  penetration: number;
  /** Share of opposing sectors that make contact. */
  coverage: number;
  /** Smaller side's contact area ÷ larger side's (1 for partial scans). */
  balance: number;
  /** Saturating contact-area term: area / (area + 4 mm²). */
  amount: number;
}

export interface ContactStats {
  contactCount: number;
  nearContactCount: number;
  interferenceCount: number;
  clearanceCount: number;
  lightContactCount: number;
  minSignedDistance: number;
  maxSignedDistance: number;
  meanSignedDistance: number;
  meanContactDistance: number;
  sampledVertices: number;
  /** Upper-arch surface area at ≤ LIGHT_CONTACT_MM (includes interference), mm². */
  contactAreaMm2: number;
  interferenceAreaMm2: number;
  maxPenetrationMm: number;
  rightContactAreaMm2: number;
  leftContactAreaMm2: number;
  sectorContactAreaMm2: number[];
  sectorsPresent: number;
  sectorsWithContact: number;
  qualityScore: number;
  quality: QualityBreakdown;
}

export type AdjustmentAxis = keyof JawTransform;

export interface ContactAdjustment {
  id: number;
  rank: number;
  label: string;
  axis: AdjustmentAxis;
  direction: 1 | -1;
  step: number;
  /** Reduction of the area-weighted mean gap in the contact zone (mm). */
  improvementMm: number;
  currentMinGap: number;
  projectedMinGap: number;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface ContactAnalysisResult {
  coloredUpper: THREE.BufferGeometry;
  coloredLower: THREE.BufferGeometry;
  distances: Float32Array;
  stats: ContactStats;
}

export interface ExclusionRegion {
  id: number;
  center: Vec3;
  radiusMm: number;
}

/** Important bite-contact zones the optimizer must seat first. */
export type PriorityRegion = ExclusionRegion;

export interface ContactThresholds {
  contactMm?: number;
  nearContactMm?: number;
  lightContactMm?: number;
  clearanceCapMm?: number;
  /** Coarse sampling budget for the upper arch (contact zone is always measured exactly). */
  maxVertices?: number;
  maxLowerVertices?: number;
  /** Painted regions left out of the summary statistics. */
  exclusionRegions?: ExclusionRegion[];
  upperBvh?: MeshBVH;
  colorLowerJaw?: boolean;
  /** Segmental scan: left/right balance is not scored. */
  partial?: boolean;
  onProgress?: (done: number, total: number) => void;
}

export function jawTransformKey(t: JawTransform): string {
  return `${t.tx},${t.ty},${t.tz},${t.rx},${t.ry},${t.rz}`;
}

export type ArchScanKind = "full_arch" | "partial_segment";

export type SegmentSide = "auto" | "left" | "right" | "anterior" | "posterior";

export interface ArchScanProfile {
  kind: ArchScanKind;
  spanX: number;
  spanY: number;
  spanZ: number;
  diagonal: number;
  vertexCount: number;
  suggestedSide: "left" | "right" | "center" | "unknown";
  /** 0–1 confidence that this is a partial / segmental scan. */
  partialConfidence: number;
}

/**
 * Estimate whether a mesh is a full arch or a segmental scan. Spans are only
 * anatomically meaningful once the mesh is in the bite frame (X lateral, Y A-P).
 */
export function analyzeArchScan(geometry: THREE.BufferGeometry): ArchScanProfile {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const size = new THREE.Vector3();
  box.getSize(size);
  const spanX = size.x;
  const spanY = size.y;
  const spanZ = size.z;
  const diagonal = size.length();
  const vertexCount = geometry.getAttribute("position")?.count ?? 0;
  const lateral = Math.max(spanX, spanY);

  // Typical full arch ~55–80 mm lateral; quadrant / 4 molars ~18–40 mm.
  let partialConfidence = 0;
  if (lateral < 28) partialConfidence = 0.95;
  else if (lateral < 38) partialConfidence = 0.8;
  else if (lateral < 48) partialConfidence = 0.55;
  else if (diagonal < 55) partialConfidence = 0.45;

  const center = new THREE.Vector3();
  box.getCenter(center);
  let suggestedSide: ArchScanProfile["suggestedSide"] = "unknown";
  if (partialConfidence >= 0.45) {
    if (Math.abs(center.x) > Math.abs(center.y) * 0.6) {
      suggestedSide = center.x >= 0 ? "right" : "left";
    } else {
      suggestedSide = "center";
    }
  }

  return {
    kind: partialConfidence >= 0.5 ? "partial_segment" : "full_arch",
    spanX,
    spanY,
    spanZ,
    diagonal,
    vertexCount,
    suggestedSide,
    partialConfidence,
  };
}

export function resolveArchKind(
  upper: THREE.BufferGeometry,
  lower: THREE.BufferGeometry,
  mode: "auto" | "full" | "partial"
): { kind: ArchScanKind; upperProfile: ArchScanProfile; lowerProfile: ArchScanProfile } {
  const upperProfile = analyzeArchScan(upper);
  const lowerProfile = analyzeArchScan(lower);
  if (mode === "full") return { kind: "full_arch", upperProfile, lowerProfile };
  if (mode === "partial") return { kind: "partial_segment", upperProfile, lowerProfile };
  const conf = Math.max(upperProfile.partialConfidence, lowerProfile.partialConfidence);
  return {
    kind: conf >= 0.5 ? "partial_segment" : "full_arch",
    upperProfile,
    lowerProfile,
  };
}

function vec3Dist(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function isPointExcluded(point: Vec3, regions: ExclusionRegion[] | undefined): boolean {
  if (!regions?.length) return false;
  for (const r of regions) {
    if (vec3Dist(point, r.center) <= r.radiusMm) return true;
  }
  return false;
}

/** Soft weight: 1 outside, up to `peakWeight` at priority center. */
export function priorityWeightAtPoint(
  point: Vec3,
  regions: PriorityRegion[] | undefined,
  peakWeight = 10
): number {
  if (!regions?.length) return 1;
  let best = 1;
  for (const r of regions) {
    const d = vec3Dist(point, r.center);
    const radius = Math.max(0.5, r.radiusMm);
    if (d > radius * 1.35) continue;
    const t = Math.max(0, 1 - d / (radius * 1.35));
    const w = 1 + (peakWeight - 1) * t * t;
    if (w > best) best = w;
  }
  return best;
}

export function isPointInPriority(point: Vec3, regions: PriorityRegion[] | undefined): boolean {
  return priorityWeightAtPoint(point, regions, 2) > 1.15;
}

export const CONTACT_CLASS_META: Record<ContactClass, { label: string; color: string; short: string }> = {
  interference: { label: "Interference (penetration)", color: "#7f1d1d", short: "Interfere" },
  max_contact: { label: "Maximum contact", color: "#ef4444", short: "Max" },
  light_contact: { label: "Light contact", color: "#f97316", short: "Light" },
  near_contact: { label: "Near contact", color: "#eab308", short: "Near" },
  clearance: { label: "Clearance", color: "#3b82f6", short: "Clear" },
};

export function colorForContactRegion(cls: ContactClass, out: THREE.Color): THREE.Color {
  switch (cls) {
    case "interference":
      return out.setRGB(0.93, 0.15, 0.2);
    case "max_contact":
      return out.setRGB(0.98, 0.35, 0.12);
    case "light_contact":
      return out.setRGB(0.99, 0.72, 0.15);
    case "near_contact":
      return out.setRGB(0.95, 0.82, 0.12);
    default:
      return out.setRGB(0.23, 0.51, 0.96);
  }
}

export function classifyContact(
  signed: number,
  contactMm = CONTACT_MM,
  lightMm = LIGHT_CONTACT_MM,
  nearMm = NEAR_CONTACT_MM
): ContactClass {
  if (signed < -PENETRATION_TOLERANCE_MM) return "interference";
  if (signed <= contactMm) return "max_contact";
  if (signed <= lightMm) return "light_contact";
  if (signed <= nearMm) return "near_contact";
  return "clearance";
}

/** Distance gradient: red (interference) → orange (contact) → yellow (near) → blue (clearance). */
export function colorForSignedDistance(
  d: number,
  contactMm: number,
  nearMm: number,
  clearanceCap: number,
  out: THREE.Color
): THREE.Color {
  if (d < -PENETRATION_TOLERANCE_MM) {
    const t = Math.min(1, -d / 0.25);
    return out.setRGB(0.45 + 0.55 * t, 0.02, 0.08 * (1 - t));
  }
  if (d <= contactMm) {
    const t = Math.max(0, d) / (contactMm || 1e-6);
    return out.setRGB(0.95, 0.08 + 0.12 * t, 0.05);
  }
  if (d <= nearMm) {
    const t = (d - contactMm) / (nearMm - contactMm || 1e-6);
    if (t < 0.5) {
      const u = t * 2;
      return out.setRGB(0.95, 0.2 + 0.4 * u, 0.05);
    }
    const u = (t - 0.5) * 2;
    return out.setRGB(0.95, 0.6 + 0.35 * u, 0.05 + 0.1 * u);
  }
  const t = Math.min(1, (d - nearMm) / (clearanceCap - nearMm || 1));
  if (t < 0.35) {
    const u = t / 0.35;
    return out.setRGB(0.35 * (1 - u), 0.85, 0.2 + 0.4 * u);
  }
  if (t < 0.7) {
    const u = (t - 0.35) / 0.35;
    return out.setRGB(0.05, 0.65 + 0.15 * (1 - u), 0.6 + 0.3 * u);
  }
  const u = (t - 0.7) / 0.3;
  return out.setRGB(0.12 + 0.25 * u, 0.28 + 0.2 * u, 0.75 * (1 - u) + 0.45 * u);
}

/** Arch sector in the bite frame (+X patient right, +Y anterior). */
export function archSector(x: number, y: number): number {
  let a = THREE.MathUtils.radToDeg(Math.atan2(y, x));
  if (a < -90) a += 360;
  if (a < 25) return 0;
  if (a < 65) return 1;
  if (a < 115) return 2;
  if (a < 155) return 3;
  return 4;
}

// ---------------------------------------------------------------------------
// Signed distance

interface SurfaceContext {
  geometry: THREE.BufferGeometry;
  bvh: MeshBVH;
  matrix: THREE.Matrix4;
  inverse: THREE.Matrix4;
  normalMatrix: THREE.Matrix3;
  positions: THREE.BufferAttribute;
  normals: THREE.BufferAttribute | null;
}

function makeSurfaceContext(
  geometry: THREE.BufferGeometry,
  bvh: MeshBVH,
  matrix: THREE.Matrix4
): SurfaceContext {
  if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();
  return {
    geometry,
    bvh,
    matrix: matrix.clone(),
    inverse: matrix.clone().invert(),
    normalMatrix: new THREE.Matrix3().getNormalMatrix(matrix),
    positions: geometry.getAttribute("position") as THREE.BufferAttribute,
    normals: (geometry.getAttribute("normal") as THREE.BufferAttribute) ?? null,
  };
}

interface SignedSample {
  found: boolean;
  signed: number;
  /** Surface normal at the closest point (world). */
  normal: THREE.Vector3;
  /** Closest point on the surface (world). */
  closest: THREE.Vector3;
}

const sd = {
  local: new THREE.Vector3(),
  a: new THREE.Vector3(),
  b: new THREE.Vector3(),
  c: new THREE.Vector3(),
  na: new THREE.Vector3(),
  nb: new THREE.Vector3(),
  nc: new THREE.Vector3(),
  bary: new THREE.Vector3(),
  toQuery: new THREE.Vector3(),
  hit: { point: new THREE.Vector3(), distance: 0, faceIndex: 0 },
};

function newSignedSample(): SignedSample {
  return {
    found: false,
    signed: DIST_QUERY_CAP_MM,
    normal: new THREE.Vector3(),
    closest: new THREE.Vector3(),
  };
}

/**
 * Signed distance from a world point to a surface. The side is decided by the
 * smooth (barycentric-interpolated) vertex normal at the closest point, which is
 * stable across triangle edges where single face normals flip.
 */
function signedDistance(
  ctx: SurfaceContext,
  queryWorld: THREE.Vector3,
  out: SignedSample,
  capMm = DIST_QUERY_CAP_MM
): SignedSample {
  sd.local.copy(queryWorld).applyMatrix4(ctx.inverse);
  // BVH cost grows with the search radius, so callers pass the smallest cap they need.
  const hit = ctx.bvh.closestPointToPoint(sd.local, sd.hit, 0, capMm);
  if (!hit) {
    out.found = false;
    out.signed = DIST_QUERY_CAP_MM;
    return out;
  }
  const f = hit.faceIndex;
  const ia = triangleCorner(ctx.geometry, f, 0);
  const ib = triangleCorner(ctx.geometry, f, 1);
  const ic = triangleCorner(ctx.geometry, f, 2);
  sd.a.fromBufferAttribute(ctx.positions, ia);
  sd.b.fromBufferAttribute(ctx.positions, ib);
  sd.c.fromBufferAttribute(ctx.positions, ic);

  const n = out.normal;
  let haveNormal = false;
  if (ctx.normals && THREE.Triangle.getBarycoord(hit.point, sd.a, sd.b, sd.c, sd.bary)) {
    sd.na.fromBufferAttribute(ctx.normals, ia).multiplyScalar(sd.bary.x);
    sd.nb.fromBufferAttribute(ctx.normals, ib).multiplyScalar(sd.bary.y);
    sd.nc.fromBufferAttribute(ctx.normals, ic).multiplyScalar(sd.bary.z);
    n.copy(sd.na).add(sd.nb).add(sd.nc);
    haveNormal = n.lengthSq() > 1e-12;
  }
  if (!haveNormal) {
    n.subVectors(sd.b, sd.a).cross(sd.toQuery.subVectors(sd.c, sd.a));
  }
  n.applyMatrix3(ctx.normalMatrix).normalize();
  out.closest.copy(hit.point).applyMatrix4(ctx.matrix);
  const side = sd.toQuery.subVectors(queryWorld, out.closest).dot(n);
  out.found = true;
  out.signed = side >= 0 ? hit.distance : -hit.distance;
  return out;
}

// ---------------------------------------------------------------------------
// Heat map

interface YieldBudget {
  last: number;
}

const now = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

async function maybeYield(budget: YieldBudget) {
  if (now() - budget.last < 14) return;
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
  budget.last = now();
}

interface DistanceFieldOptions {
  maxSamples: number;
  onProgress?: (fraction: number) => void;
  budget: YieldBudget;
}

/**
 * Per-vertex signed distance from `source` (posed by sourceMatrix) to `target`.
 * A coarse pass is interpolated over the mesh, then every vertex that may be in or
 * near contact is measured exactly, and isolated sign flips are removed.
 */
async function computeVertexDistances(
  source: THREE.BufferGeometry,
  sourceMatrix: THREE.Matrix4,
  target: SurfaceContext,
  opts: DistanceFieldOptions
): Promise<Float32Array> {
  const pos = source.getAttribute("position") as THREE.BufferAttribute;
  const n = pos.count;
  const dist = new Float32Array(n).fill(Number.NaN);
  const exact = new Uint8Array(n);
  const q = new THREE.Vector3();
  const sample = newSignedSample();
  const stride = Math.max(1, Math.ceil(n / Math.max(1, opts.maxSamples)));

  const measure = (i: number) => {
    q.fromBufferAttribute(pos, i).applyMatrix4(sourceMatrix);
    dist[i] = signedDistance(target, q, sample).signed;
    exact[i] = 1;
  };

  let done = 0;
  const coarseTotal = Math.ceil(n / stride);
  for (let i = 0; i < n; i += stride) {
    measure(i);
    if (++done % 512 === 0) {
      opts.onProgress?.(0.75 * (done / coarseTotal));
      await maybeYield(opts.budget);
    }
  }

  const adjacency = getVertexAdjacency(source);
  if (stride > 1) {
    if (adjacency) {
      for (let pass = 0; pass < 64; pass++) {
        let missing = 0;
        for (let i = 0; i < n; i++) {
          if (!Number.isNaN(dist[i])) continue;
          let sum = 0;
          let k = 0;
          for (let e = adjacency.offsets[i]; e < adjacency.offsets[i + 1]; e++) {
            const v = dist[adjacency.neighbors[e]];
            if (!Number.isNaN(v)) {
              sum += v;
              k++;
            }
          }
          if (k > 0) dist[i] = sum / k;
          else missing++;
        }
        if (missing === 0) break;
      }
    }
    for (let i = 0; i < n; i++) {
      if (Number.isNaN(dist[i])) measure(i);
    }

    let refineTotal = 0;
    for (let i = 0; i < n; i++) if (!exact[i] && dist[i] < REFINE_BELOW_MM) refineTotal++;
    let refined = 0;
    for (let i = 0; i < n; i++) {
      if (exact[i] || dist[i] >= REFINE_BELOW_MM) continue;
      measure(i);
      if (++refined % 512 === 0) {
        opts.onProgress?.(0.75 + 0.25 * (refined / Math.max(1, refineTotal)));
        await maybeYield(opts.budget);
      }
    }
  }

  if (adjacency) {
    for (let i = 0; i < n; i++) {
      if (dist[i] >= -PENETRATION_TOLERANCE_MM) continue;
      let negativeNeighbours = 0;
      for (let e = adjacency.offsets[i]; e < adjacency.offsets[i + 1]; e++) {
        if (dist[adjacency.neighbors[e]] < 0) negativeNeighbours++;
      }
      if (negativeNeighbours === 0) dist[i] = -dist[i];
    }
  }

  opts.onProgress?.(1);
  return dist;
}

function paintDistances(
  geometry: THREE.BufferGeometry,
  distances: Float32Array,
  contactMm: number,
  nearMm: number,
  clearanceCap: number
) {
  const colors = new Float32Array(distances.length * 3);
  const c = new THREE.Color();
  for (let i = 0; i < distances.length; i++) {
    colorForSignedDistance(distances[i], contactMm, nearMm, clearanceCap, c);
    colors[i * 3] = c.r;
    colors[i * 3 + 1] = c.g;
    colors[i * 3 + 2] = c.b;
  }
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  if (!geometry.getAttribute("normal")) geometry.computeVertexNormals();
}

// ---------------------------------------------------------------------------
// Clinical metrics

export function computeQuality(
  metrics: {
    maxPenetrationMm: number;
    contactAreaMm2: number;
    rightContactAreaMm2: number;
    leftContactAreaMm2: number;
    sectorsPresent: number;
    sectorsWithContact: number;
  },
  partial: boolean
): { score: number; breakdown: QualityBreakdown } {
  const penetration = THREE.MathUtils.clamp(1 - (metrics.maxPenetrationMm - 0.02) / 0.23, 0, 1);
  const coverage =
    metrics.sectorsPresent > 0 ? metrics.sectorsWithContact / metrics.sectorsPresent : 0;
  const big = Math.max(metrics.rightContactAreaMm2, metrics.leftContactAreaMm2);
  const small = Math.min(metrics.rightContactAreaMm2, metrics.leftContactAreaMm2);
  const balance = partial ? 1 : big > 0 ? small / big : 0;
  const amount = metrics.contactAreaMm2 / (metrics.contactAreaMm2 + 4);
  const score = Math.round(
    100 * penetration * (0.35 * coverage + 0.25 * balance + 0.4 * amount)
  );
  return { score, breakdown: { penetration, coverage, balance, amount } };
}

interface MetricAccumulator {
  contactArea: number;
  interferenceArea: number;
  right: number;
  left: number;
  sectorArea: number[];
  sectorPresent: boolean[];
  minD: number;
}

const newAccumulator = (): MetricAccumulator => ({
  contactArea: 0,
  interferenceArea: 0,
  right: 0,
  left: 0,
  sectorArea: [0, 0, 0, 0, 0],
  sectorPresent: [false, false, false, false, false],
  minD: Infinity,
});

function accumulate(
  acc: MetricAccumulator,
  x: number,
  y: number,
  d: number,
  area: number,
  lightMm: number
) {
  if (d < acc.minD) acc.minD = d;
  const s = archSector(x, y);
  if (d < SECTOR_PRESENT_MM) acc.sectorPresent[s] = true;
  if (d <= lightMm) {
    acc.contactArea += area;
    acc.sectorArea[s] += area;
    if (x >= 0) acc.right += area;
    else acc.left += area;
  }
  if (d < -PENETRATION_TOLERANCE_MM) acc.interferenceArea += area;
}

function finishMetrics(acc: MetricAccumulator, partial: boolean) {
  const sectorsPresent = acc.sectorPresent.filter(Boolean).length;
  const sectorsWithContact = acc.sectorArea.filter((a, i) => acc.sectorPresent[i] && a >= SECTOR_CONTACT_MIN_MM2).length;
  const maxPenetrationMm = Number.isFinite(acc.minD) ? Math.max(0, -acc.minD) : 0;
  const metrics = {
    maxPenetrationMm,
    contactAreaMm2: acc.contactArea,
    rightContactAreaMm2: acc.right,
    leftContactAreaMm2: acc.left,
    sectorsPresent,
    sectorsWithContact,
  };
  return { metrics, quality: computeQuality(metrics, partial) };
}

function summarizeContacts(
  upper: THREE.BufferGeometry,
  upperMatrix: THREE.Matrix4,
  distances: Float32Array,
  opts: {
    contactMm: number;
    lightMm: number;
    nearMm: number;
    exclusions?: ExclusionRegion[];
    partial: boolean;
  }
): ContactStats {
  const pos = upper.getAttribute("position") as THREE.BufferAttribute;
  const areas = getVertexAreas(upper);
  const p = new THREE.Vector3();
  const acc = newAccumulator();
  let contactCount = 0;
  let nearContactCount = 0;
  let lightContactCount = 0;
  let interferenceCount = 0;
  let clearanceCount = 0;
  let maxD = -Infinity;
  let sumD = 0;
  let sumContactD = 0;
  let contactSampleN = 0;
  let sampled = 0;

  for (let i = 0; i < distances.length; i++) {
    const d = distances[i];
    if (Number.isNaN(d)) continue;
    p.fromBufferAttribute(pos, i).applyMatrix4(upperMatrix);
    if (opts.exclusions?.length && isPointExcluded(p, opts.exclusions)) continue;
    sampled++;
    sumD += d;
    if (d > maxD) maxD = d;
    accumulate(acc, p.x, p.y, d, areas[i], opts.lightMm);
    const cls = classifyContact(d, opts.contactMm, opts.lightMm, opts.nearMm);
    if (cls === "interference") interferenceCount++;
    else if (cls === "max_contact") {
      contactCount++;
      sumContactD += d;
      contactSampleN++;
    } else if (cls === "light_contact") {
      lightContactCount++;
      sumContactD += d;
      contactSampleN++;
    } else if (cls === "near_contact") nearContactCount++;
    else clearanceCount++;
  }

  const { metrics, quality } = finishMetrics(acc, opts.partial);
  return {
    contactCount,
    nearContactCount,
    lightContactCount,
    interferenceCount,
    clearanceCount,
    minSignedDistance: Number.isFinite(acc.minD) ? acc.minD : 0,
    maxSignedDistance: Number.isFinite(maxD) ? maxD : 0,
    meanSignedDistance: sampled > 0 ? sumD / sampled : 0,
    meanContactDistance: contactSampleN > 0 ? sumContactD / contactSampleN : 0,
    sampledVertices: sampled,
    contactAreaMm2: metrics.contactAreaMm2,
    interferenceAreaMm2: acc.interferenceArea,
    maxPenetrationMm: metrics.maxPenetrationMm,
    rightContactAreaMm2: metrics.rightContactAreaMm2,
    leftContactAreaMm2: metrics.leftContactAreaMm2,
    sectorContactAreaMm2: acc.sectorArea.slice(),
    sectorsPresent: metrics.sectorsPresent,
    sectorsWithContact: metrics.sectorsWithContact,
    qualityScore: quality.score,
    quality: quality.breakdown,
  };
}

export function estimateBiteQualityFromStats(stats: ContactStats): number {
  return stats.qualityScore;
}

/**
 * Heat-map both arches by signed distance to the opposing arch and summarise the
 * occlusion (contact area, balance, sector coverage, penetration, quality).
 */
export async function analyzeOcclusalContacts(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  baseLowerBvh: MeshBVH,
  upperMatrix: THREE.Matrix4,
  lowerMatrix: THREE.Matrix4,
  options: ContactThresholds = {}
): Promise<ContactAnalysisResult> {
  const contactMm = options.contactMm ?? CONTACT_MM;
  const nearMm = options.nearContactMm ?? NEAR_CONTACT_MM;
  const lightMm = options.lightContactMm ?? LIGHT_CONTACT_MM;
  const clearanceCap = options.clearanceCapMm ?? COLOUR_CLEARANCE_CAP_MM;
  const maxVertices = options.maxVertices ?? 60_000;
  const colorLower = options.colorLowerJaw !== false;
  const maxLowerVertices = options.maxLowerVertices ?? Math.max(4_000, Math.floor(maxVertices * 0.5));
  const budget: YieldBudget = { last: now() };
  const PROGRESS_SCALE = 1000;
  const total = colorLower ? PROGRESS_SCALE * 2 : PROGRESS_SCALE;

  const lowerCtx = makeSurfaceContext(baseLower, baseLowerBvh, lowerMatrix);
  const distances = await computeVertexDistances(baseUpper, upperMatrix, lowerCtx, {
    maxSamples: maxVertices,
    budget,
    onProgress: (f) => options.onProgress?.(Math.round(f * PROGRESS_SCALE), total),
  });
  const coloredUpper = baseUpper.clone();
  paintDistances(coloredUpper, distances, contactMm, nearMm, clearanceCap);

  const coloredLower = baseLower.clone();
  if (colorLower) {
    const upperBvh = options.upperBvh ?? buildBvh(baseUpper);
    const upperCtx = makeSurfaceContext(baseUpper, upperBvh, upperMatrix);
    const lowerDistances = await computeVertexDistances(baseLower, lowerMatrix, upperCtx, {
      maxSamples: maxLowerVertices,
      budget,
      onProgress: (f) =>
        options.onProgress?.(PROGRESS_SCALE + Math.round(f * PROGRESS_SCALE), total),
    });
    paintDistances(coloredLower, lowerDistances, contactMm, nearMm, clearanceCap);
  } else if (!coloredLower.getAttribute("normal")) {
    coloredLower.computeVertexNormals();
  }

  const stats = summarizeContacts(baseUpper, upperMatrix, distances, {
    contactMm,
    lightMm,
    nearMm,
    exclusions: options.exclusionRegions,
    partial: Boolean(options.partial),
  });

  return { coloredUpper, coloredLower, distances, stats };
}

// ---------------------------------------------------------------------------
// Bite optimization: robust contact solver

export type BiteOptimizeQuality = "fast" | "balanced" | "accurate";

export interface BiteOptimizeProgress {
  phase: "coarse" | "medium" | "fine" | "polish" | "couple" | "finalize";
  iteration: number;
  maxIterations: number;
  minGap: number;
  objective: number;
  appliedSteps: number;
  qualityScore?: number;
}

export interface BiteOptimizationResult {
  upperTx: JawTransform;
  lowerTx: JawTransform;
  appliedSteps: number;
  finalMinGap: number;
  maxContactCount: number;
  interferenceCount: number;
  lightContactCount: number;
  nearContactCount: number;
  qualityScore: number;
  contactAreaMm2: number;
  rightContactAreaMm2: number;
  leftContactAreaMm2: number;
  sectorsWithContact: number;
  sectorsPresent: number;
}

interface Candidates {
  count: number;
  /** Upper-arch base positions. */
  base: Float32Array;
  /** Normalised weights (balance across sectors × priority). */
  weight: Float32Array;
  /** Represented surface area (mm²). */
  area: Float32Array;
}

interface GatherOptions {
  poolBudget: number;
  maxDistMm: number;
  budget: number;
  exclusions?: ExclusionRegion[];
  priorities?: PriorityRegion[];
  partial: boolean;
}

function gatherCandidates(
  baseUpper: THREE.BufferGeometry,
  upperMatrix: THREE.Matrix4,
  lowerCtx: SurfaceContext,
  opts: GatherOptions
): Candidates {
  const pos = baseUpper.getAttribute("position") as THREE.BufferAttribute;
  const areas = getVertexAreas(baseUpper);
  const stride = Math.max(1, Math.ceil(pos.count / opts.poolBudget));
  const p = new THREE.Vector3();
  const sample = newSignedSample();
  const kept: number[] = [];
  const keptPriority: boolean[] = [];

  for (let i = 0; i < pos.count; i += stride) {
    p.fromBufferAttribute(pos, i).applyMatrix4(upperMatrix);
    if (opts.exclusions?.length && isPointExcluded(p, opts.exclusions)) continue;
    signedDistance(lowerCtx, p, sample, opts.maxDistMm + 0.05);
    if (!sample.found || sample.signed > opts.maxDistMm) continue;
    kept.push(i);
    keptPriority.push(isPointInPriority(p, opts.priorities));
  }

  let chosen = kept;
  if (kept.length > opts.budget) {
    const step = kept.length / opts.budget;
    const pick = new Set<number>();
    for (let k = 0; k < opts.budget; k++) pick.add(Math.floor(k * step));
    keptPriority.forEach((isPriority, k) => {
      if (isPriority) pick.add(k);
    });
    chosen = [...pick].sort((a, b) => a - b).map((k) => kept[k]);
  }
  const areaScale = stride * (kept.length / Math.max(1, chosen.length));

  const count = chosen.length;
  const base = new Float32Array(count * 3);
  const area = new Float32Array(count);
  const weight = new Float32Array(count);
  const sector = new Uint8Array(count);
  const sectorTotals = [0, 0, 0, 0, 0];
  for (let k = 0; k < count; k++) {
    const i = chosen[k];
    base[k * 3] = pos.getX(i);
    base[k * 3 + 1] = pos.getY(i);
    base[k * 3 + 2] = pos.getZ(i);
    area[k] = areas[i] * areaScale;
    p.fromBufferAttribute(pos, i).applyMatrix4(upperMatrix);
    sector[k] = opts.partial ? 0 : archSector(p.x, p.y);
    sectorTotals[sector[k]] += area[k];
  }
  let wSum = 0;
  for (let k = 0; k < count; k++) {
    p.set(base[k * 3], base[k * 3 + 1], base[k * 3 + 2]).applyMatrix4(upperMatrix);
    const w =
      (area[k] / Math.max(1e-9, sectorTotals[sector[k]])) *
      priorityWeightAtPoint(p, opts.priorities, 8);
    weight[k] = w;
    wSum += w;
  }
  if (wSum > 0) for (let k = 0; k < count; k++) weight[k] /= wSum;
  return { count, base, weight, area };
}

const PENETRATION_WEIGHT = 50;
/** Beyond this the Welsch attraction is ~0 even at the widest sigma. */
const POSE_QUERY_CAP_MM = 3;
/** Welsch weight at 4σ is e⁻¹⁶, so nothing past it moves the solve. */
const capForSigma = (sigma: number) => Math.min(POSE_QUERY_CAP_MM, Math.max(0.4, 4 * sigma));

interface PoseEvaluation {
  energy: number;
  minD: number;
  A?: Float64Array;
  b?: Float64Array;
}

function evaluatePose(
  c: Candidates,
  upperMatrix: THREE.Matrix4,
  lowerCtx: SurfaceContext,
  movable: "upper" | "lower",
  sigma: number,
  withSystem: boolean,
  capMm = POSE_QUERY_CAP_MM
): PoseEvaluation {
  const p = new THREE.Vector3();
  const sample = newSignedSample();
  const A = withSystem ? new Float64Array(36) : undefined;
  const b = withSystem ? new Float64Array(6) : undefined;
  const J = new Float64Array(6);
  const sigma2 = sigma * sigma;
  let energy = 0;
  let minD = Infinity;

  for (let k = 0; k < c.count; k++) {
    p.set(c.base[k * 3], c.base[k * 3 + 1], c.base[k * 3 + 2]).applyMatrix4(upperMatrix);
    signedDistance(lowerCtx, p, sample, capMm);
    if (!sample.found) {
      // Beyond the cap the Welsch term has saturated at sigma².
      energy += c.weight[k] * sigma2;
      continue;
    }
    const d = sample.signed;
    if (d < minD) minD = d;
    let rho: number;
    let w: number;
    if (d < 0) {
      rho = PENETRATION_WEIGHT * d * d;
      w = PENETRATION_WEIGHT;
    } else {
      const e = Math.exp(-(d * d) / sigma2);
      rho = sigma2 * (1 - e);
      w = e;
    }
    energy += c.weight[k] * rho;
    if (!A || !b || w < 1e-8) continue;

    const n = sample.normal;
    const lever = movable === "upper" ? p : sample.closest;
    const sign = movable === "upper" ? 1 : -1;
    J[0] = sign * (lever.y * n.z - lever.z * n.y);
    J[1] = sign * (lever.z * n.x - lever.x * n.z);
    J[2] = sign * (lever.x * n.y - lever.y * n.x);
    J[3] = sign * n.x;
    J[4] = sign * n.y;
    J[5] = sign * n.z;
    const ww = c.weight[k] * w;
    for (let r = 0; r < 6; r++) {
      b[r] -= ww * J[r] * d;
      for (let col = r; col < 6; col++) A[r * 6 + col] += ww * J[r] * J[col];
    }
  }
  if (A) {
    for (let r = 0; r < 6; r++) for (let col = 0; col < r; col++) A[r * 6 + col] = A[col * 6 + r];
  }
  return { energy, minD: Number.isFinite(minD) ? minD : DIST_QUERY_CAP_MM, A, b };
}

/** Minimum signed gap of the candidates after translating the movable jaw by `s` mm along closing. */
function minGapAfterClosing(
  c: Candidates,
  upperMatrix: THREE.Matrix4,
  lowerMatrix: THREE.Matrix4,
  lowerGeo: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  movable: "upper" | "lower",
  s: number,
  capMm: number
): number {
  const shift = new THREE.Matrix4().makeTranslation(0, 0, movable === "upper" ? -s : s);
  const u = movable === "upper" ? shift.clone().multiply(upperMatrix) : upperMatrix;
  const l = movable === "lower" ? shift.clone().multiply(lowerMatrix) : lowerMatrix;
  const ctx = makeSurfaceContext(lowerGeo, lowerBvh, l);
  return evaluatePose(c, u, ctx, movable, 0.1, false, capMm).minD;
}

/** Find the closing translation `s` at which the minimum gap equals `targetGap`. */
function seatAlongClosing(
  c: Candidates,
  upperMatrix: THREE.Matrix4,
  lowerMatrix: THREE.Matrix4,
  lowerGeo: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  movable: "upper" | "lower",
  targetGap: number,
  capMm = POSE_QUERY_CAP_MM
): number {
  const f = (s: number) =>
    minGapAfterClosing(c, upperMatrix, lowerMatrix, lowerGeo, lowerBvh, movable, s, capMm);
  const f0 = f(0);
  if (Math.abs(f0 - targetGap) < 0.002) return 0;
  let lo: number;
  let hi: number;
  if (f0 > targetGap) {
    lo = 0;
    hi = Math.min(DIST_QUERY_CAP_MM, f0 - targetGap + 0.2);
    let guard = 0;
    while (f(hi) > targetGap && guard++ < 6) hi *= 1.6;
  } else {
    hi = 0;
    lo = -(targetGap - f0 + 0.2);
    let guard = 0;
    while (f(lo) < targetGap && guard++ < 6) lo *= 1.6;
  }
  for (let it = 0; it < 24 && hi - lo > 5e-4; it++) {
    const mid = (lo + hi) / 2;
    if (f(mid) > targetGap) lo = mid;
    else hi = mid;
  }
  return lo;
}

interface SolverPreset {
  candidates: number;
  pool: number;
  iterations: number;
  sigmaMin: number;
  /** Initial attraction width (mm); smaller when polishing an already-seated bite. */
  sigmaStart?: number;
}

const QUALITY_PRESETS: Record<BiteOptimizeQuality, SolverPreset> = {
  fast: { candidates: 1500, pool: 30_000, iterations: 30, sigmaMin: 0.05 },
  balanced: { candidates: 2500, pool: 45_000, iterations: 45, sigmaMin: 0.04 },
  accurate: { candidates: 4000, pool: 70_000, iterations: 70, sigmaMin: 0.03 },
};

/** Cheap solve used to explore many starting poses before refining the best few. */
const EXPLORE_PRESET: SolverPreset = {
  candidates: 300,
  pool: 5_000,
  iterations: 14,
  sigmaMin: 0.08,
};

function phaseForSigma(sigma: number): BiteOptimizeProgress["phase"] {
  if (sigma > 0.3) return "coarse";
  if (sigma > 0.12) return "medium";
  if (sigma > 0.06) return "fine";
  return "polish";
}

function sampleQuality(
  c: Candidates,
  upperMatrix: THREE.Matrix4,
  lowerCtx: SurfaceContext,
  partial: boolean
): {
  minD: number;
  quality: number;
  max: number;
  light: number;
  near: number;
  interference: number;
  metrics: ReturnType<typeof finishMetrics>["metrics"];
} {
  const p = new THREE.Vector3();
  const sample = newSignedSample();
  const acc = newAccumulator();
  let max = 0;
  let light = 0;
  let near = 0;
  let interference = 0;
  for (let k = 0; k < c.count; k++) {
    p.set(c.base[k * 3], c.base[k * 3 + 1], c.base[k * 3 + 2]).applyMatrix4(upperMatrix);
    signedDistance(lowerCtx, p, sample, SECTOR_PRESENT_MM + 0.05);
    if (!sample.found) continue;
    const d = sample.signed;
    accumulate(acc, p.x, p.y, d, c.area[k], LIGHT_CONTACT_MM);
    const cls = classifyContact(d);
    if (cls === "interference") interference++;
    else if (cls === "max_contact") max++;
    else if (cls === "light_contact") light++;
    else if (cls === "near_contact") near++;
  }
  const { quality, metrics } = finishMetrics(acc, partial);
  return {
    minD: Number.isFinite(acc.minD) ? acc.minD : DIST_QUERY_CAP_MM,
    quality: quality.score,
    max,
    light,
    near,
    interference,
    metrics,
  };
}

/** How far the movable jaw must close (mm) before the bounding boxes touch; 0 if they already overlap. */
function boxClosingGap(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  upperM: THREE.Matrix4,
  lowerM: THREE.Matrix4
): number {
  if (!baseUpper.boundingBox) baseUpper.computeBoundingBox();
  if (!baseLower.boundingBox) baseLower.computeBoundingBox();
  const u = baseUpper.boundingBox!.clone().applyMatrix4(upperM);
  const l = baseLower.boundingBox!.clone().applyMatrix4(lowerM);
  return Math.max(0, u.min.z - l.max.z);
}

async function runContactSolver(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  upperTx: JawTransform,
  lowerTx: JawTransform,
  movable: "upper" | "lower",
  options: {
    exclusionRegions?: ExclusionRegion[];
    priorityRegions?: PriorityRegion[];
    quality?: BiteOptimizeQuality;
    partial?: boolean;
    onProgress?: (progress: BiteOptimizeProgress) => void;
    shouldCancel?: () => boolean;
    preset?: SolverPreset;
    /** Skip the initial close-to-contact step (pose is already seated). */
    skipSeat?: boolean;
    /** Skip the dense no-interference lift (exploration only; results are re-solved). */
    skipFinalLift?: boolean;
  }
): Promise<BiteOptimizationResult> {
  const preset = options.preset ?? QUALITY_PRESETS[options.quality ?? "balanced"];
  const partial = Boolean(options.partial);
  let upperM = jawTransformToMatrix(upperTx);
  let lowerM = jawTransformToMatrix(lowerTx);
  const origin = new THREE.Vector3();
  const pause = () => new Promise<void>((resolve) => setTimeout(resolve, 0));
  const ctxFor = (l: THREE.Matrix4) => makeSurfaceContext(baseLower, lowerBvh, l);
  const gather = (maxDistMm: number, budget = preset.candidates, pool = preset.pool) =>
    gatherCandidates(baseUpper, upperM, ctxFor(lowerM), {
      poolBudget: pool,
      maxDistMm,
      budget,
      exclusions: options.exclusionRegions,
      priorities: options.priorityRegions,
      partial,
    });
  const applyClosing = (s: number) => {
    const shift = new THREE.Matrix4().makeTranslation(0, 0, movable === "upper" ? -s : s);
    if (movable === "upper") upperM = shift.multiply(upperM);
    else lowerM = shift.multiply(lowerM);
  };

  // 1. Seat the jaws along the closing axis to first contact.
  const seatPool = Math.min(preset.pool, 20_000);
  const gatherRadius = (sigma: number) => Math.min(2.5, capForSigma(sigma) + 0.6);
  let candidates = options.skipSeat
    ? gather(gatherRadius(preset.sigmaStart ?? 0.5))
    : gather(POSE_QUERY_CAP_MM, preset.candidates, seatPool);
  if (candidates.count === 0 && !options.skipSeat) {
    candidates = gather(DIST_QUERY_CAP_MM, preset.candidates, seatPool);
  }
  if (candidates.count === 0) {
    // Jaws too far apart for distance queries: drop until the boxes touch, then step closer.
    applyClosing(boxClosingGap(baseUpper, baseLower, upperM, lowerM));
    candidates = gather(DIST_QUERY_CAP_MM, preset.candidates, seatPool);
    for (let k = 0; k < 8 && candidates.count === 0; k++) {
      applyClosing(DIST_QUERY_CAP_MM * 0.5);
      candidates = gather(DIST_QUERY_CAP_MM, preset.candidates, seatPool);
    }
  }
  if (candidates.count > 0 && !options.skipSeat) {
    applyClosing(seatAlongClosing(candidates, upperM, lowerM, baseLower, lowerBvh, movable, 0.02));
  }
  await pause();

  // 2. Robust Gauss-Newton contact solve with annealed attraction width.
  let sigma = preset.sigmaStart ?? 0.5;
  if (!options.skipSeat) candidates = gather(gatherRadius(sigma));
  let lambda = 1e-3;
  let appliedSteps = 0;
  let sinceGather = 0;
  const stiffAxes = partial ? [2, 3, 4] : [];

  for (let iter = 0; iter < preset.iterations && candidates.count >= 6; iter++) {
    if (options.shouldCancel?.()) break;
    const cap = capForSigma(sigma);
    const current = evaluatePose(candidates, upperM, ctxFor(lowerM), movable, sigma, true, cap);
    const A = current.A!;
    const b = current.b!;
    let accepted = false;
    let stepSize = 0;

    for (let attempt = 0; attempt < 6 && !accepted; attempt++) {
      const damped = new Float64Array(A);
      let trace = 0;
      for (let k = 0; k < 6; k++) trace += A[k * 6 + k];
      for (let k = 0; k < 6; k++) {
        damped[k * 6 + k] += lambda * A[k * 6 + k] + 1e-12 * (trace + 1);
      }
      for (const k of stiffAxes) damped[k * 6 + k] += 0.15 * (trace / 6);
      const x = solveLinearSystem(damped, b, 6);
      if (!x) {
        lambda *= 5;
        continue;
      }
      const w = new THREE.Vector3(x[0], x[1], x[2]);
      const t = new THREE.Vector3(x[3], x[4], x[5]);
      const maxT = 0.3 * sigma + 0.02;
      const maxW = THREE.MathUtils.degToRad(0.6);
      if (t.length() > maxT) t.setLength(maxT);
      if (w.length() > maxW) w.setLength(maxW);

      const inc = rigidIncrement(w, t, origin);
      const trialU = movable === "upper" ? inc.clone().multiply(upperM) : upperM;
      const trialL = movable === "lower" ? inc.clone().multiply(lowerM) : lowerM;
      const trial = evaluatePose(candidates, trialU, ctxFor(trialL), movable, sigma, false, cap);
      if (trial.energy < current.energy - 1e-14) {
        upperM = trialU;
        lowerM = trialL;
        accepted = true;
        stepSize = t.length() + w.length() * 25;
        lambda = Math.max(1e-6, lambda / 3);
      } else {
        lambda *= 5;
      }
    }

    if (accepted) {
      appliedSteps++;
      sinceGather++;
    }
    const atMin = sigma <= preset.sigmaMin + 1e-9;
    if (atMin && (!accepted || stepSize < 2e-4)) break;
    sigma = Math.max(preset.sigmaMin, sigma * 0.82);

    if (sinceGather >= 10) {
      candidates = gather(gatherRadius(sigma));
      sinceGather = 0;
    }

    if (iter % 2 === 0 && options.onProgress) {
      const probe = sampleQuality(candidates, upperM, ctxFor(lowerM), partial);
      options.onProgress?.({
        phase: phaseForSigma(sigma),
        iteration: iter + 1,
        maxIterations: preset.iterations,
        minGap: probe.minD,
        objective: current.energy,
        appliedSteps,
        qualityScore: probe.quality,
      });
      await pause();
    }
  }

  // 3. Guarantee no residual penetration: lift along the closing axis if needed.
  const dense = options.skipFinalLift
    ? candidates
    : gather(1.0, preset.candidates * 4, preset.pool * 2);
  if (dense.count > 0 && !options.skipFinalLift) {
    // Only surface already near contact can still overlap after a small lift.
    const contactSet = gather(0.3, preset.candidates * 4, preset.pool * 2);
    const minD = contactSet.count > 0
      ? evaluatePose(contactSet, upperM, ctxFor(lowerM), movable, 0.1, false, 1).minD
      : DIST_QUERY_CAP_MM;
    if (minD < -PENETRATION_TOLERANCE_MM) {
      applyClosing(seatAlongClosing(contactSet, upperM, lowerM, baseLower, lowerBvh, movable, 0, 1));
    }
  }

  const final = sampleQuality(dense.count > 0 ? dense : candidates, upperM, ctxFor(lowerM), partial);
  options.onProgress?.({
    phase: "finalize",
    iteration: 1,
    maxIterations: 1,
    minGap: final.minD,
    objective: 0,
    appliedSteps,
    qualityScore: final.quality,
  });

  return {
    upperTx: matrixToJawTransform(upperM),
    lowerTx: matrixToJawTransform(lowerM),
    appliedSteps,
    finalMinGap: final.minD,
    maxContactCount: final.max,
    interferenceCount: final.interference,
    lightContactCount: final.light,
    nearContactCount: final.near,
    qualityScore: final.quality,
    contactAreaMm2: final.metrics.contactAreaMm2,
    rightContactAreaMm2: final.metrics.rightContactAreaMm2,
    leftContactAreaMm2: final.metrics.leftContactAreaMm2,
    sectorsWithContact: final.metrics.sectorsWithContact,
    sectorsPresent: final.metrics.sectorsPresent,
  };
}

/**
 * Seat the movable jaw into maximum intercuspation: close to first contact, then a
 * robust Gauss-Newton solve pulls near-contacts together (balanced across arch
 * sectors, weighted by priority zones) against a stiff penetration barrier, and a
 * final lift guarantees no interference beyond PENETRATION_TOLERANCE_MM.
 */
export async function optimizeBiteClosure(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  initialUpperTx: JawTransform,
  initialLowerTx: JawTransform,
  movableJaw: "upper" | "lower",
  options: {
    exclusionRegions?: ExclusionRegion[];
    priorityRegions?: PriorityRegion[];
    quality?: BiteOptimizeQuality;
    partial?: boolean;
    onProgress?: (progress: BiteOptimizeProgress) => void;
    shouldCancel?: () => boolean;
  } = {}
): Promise<BiteOptimizationResult> {
  return runContactSolver(
    baseUpper,
    baseLower,
    lowerBvh,
    initialUpperTx,
    initialLowerTx,
    movableJaw,
    options
  );
}

export const optimizeBiteClosureAsync = optimizeBiteClosure;

// ---------------------------------------------------------------------------
// Multiple bite options

export interface BiteOption extends BiteOptimizationResult {
  id: number;
  /** Upper-arch displacement from option 1 in the bite frame (mm; +X right, +Y anterior, +Z open). */
  offsetFromBestMm: Vec3;
  /** Relative rotation from option 1 (degrees). */
  rotationFromBestDeg: number;
}

export interface BiteOptionsProgress {
  stage: "explore" | "refine";
  done: number;
  total: number;
  bestQuality: number | null;
}

/** Starting offsets (bite frame) applied to the movable jaw before each solve. */
const OPTION_STARTS: Partial<JawTransform>[] = [
  {},
  { tx: 0.6 },
  { tx: -0.6 },
  { ty: 0.6 },
  { ty: -0.6 },
  { rz: 1.5 },
  { rz: -1.5 },
  { tx: 0.4, ty: 0.4 },
  { tx: -0.4, ty: -0.4 },
  { tx: 1.2 },
  { tx: -1.2 },
  { ty: 1.2 },
  { ty: -1.2 },
  { ry: 1 },
  { ry: -1 },
  { rx: 1 },
];

const STARTS_PER_QUALITY: Record<BiteOptimizeQuality, number> = {
  fast: 9,
  balanced: 13,
  accurate: 16,
};

/** Upper arch relative to lower — invariant to which jaw was moved. */
const relativePose = (upperTx: JawTransform, lowerTx: JawTransform) =>
  jawTransformToMatrix(lowerTx).invert().multiply(jawTransformToMatrix(upperTx));

function boxCorners(geometry: THREE.BufferGeometry): THREE.Vector3[] {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const { min, max } = geometry.boundingBox!;
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < 8; i++) {
    out.push(new THREE.Vector3(i & 1 ? max.x : min.x, i & 2 ? max.y : min.y, i & 4 ? max.z : min.z));
  }
  return out;
}

/** Largest displacement of the upper arch's bounding-box corners between two poses (mm). */
function poseDistance(corners: THREE.Vector3[], a: THREE.Matrix4, b: THREE.Matrix4): number {
  let worst = 0;
  const pa = new THREE.Vector3();
  const pb = new THREE.Vector3();
  for (const c of corners) {
    worst = Math.max(worst, pa.copy(c).applyMatrix4(a).distanceTo(pb.copy(c).applyMatrix4(b)));
  }
  return worst;
}

function distinctBest(
  results: BiteOptimizationResult[],
  corners: THREE.Vector3[],
  minSeparationMm: number,
  limit: number
): BiteOptimizationResult[] {
  const sorted = [...results].sort(
    (a, b) => b.qualityScore - a.qualityScore || b.contactAreaMm2 - a.contactAreaMm2
  );
  const kept: { result: BiteOptimizationResult; pose: THREE.Matrix4 }[] = [];
  for (const result of sorted) {
    const pose = relativePose(result.upperTx, result.lowerTx);
    if (kept.some((k) => poseDistance(corners, k.pose, pose) < minSeparationMm)) continue;
    kept.push({ result, pose });
    if (kept.length >= limit) break;
  }
  return kept.map((k) => k.result);
}

/**
 * Search for several distinct maximum-contact bites: solve cheaply from a spread of
 * starting offsets around the current pose, keep the best distinct seats, refine
 * those at the requested quality and return them ranked (best first).
 */
export async function findBiteOptions(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  upperTx: JawTransform,
  lowerTx: JawTransform,
  movableJaw: "upper" | "lower",
  options: {
    count?: number;
    quality?: BiteOptimizeQuality;
    partial?: boolean;
    exclusionRegions?: ExclusionRegion[];
    priorityRegions?: PriorityRegion[];
    onProgress?: (progress: BiteOptionsProgress) => void;
    shouldCancel?: () => boolean;
  } = {}
): Promise<BiteOption[]> {
  const count = options.count ?? 5;
  const quality = options.quality ?? "fast";
  const corners = boxCorners(baseUpper);
  const solverOptions = {
    partial: options.partial,
    exclusionRegions: options.exclusionRegions,
    priorityRegions: options.priorityRegions,
    shouldCancel: options.shouldCancel,
  };
  const openSign = movableJaw === "upper" ? 1 : -1;
  const starts = OPTION_STARTS.slice(0, STARTS_PER_QUALITY[quality]);

  const explored: BiteOptimizationResult[] = [];
  let bestQuality: number | null = null;
  for (let i = 0; i < starts.length; i++) {
    if (options.shouldCancel?.()) return [];
    options.onProgress?.({ stage: "explore", done: i, total: starts.length, bestQuality });
    const offset = jawTransformToMatrix({ ...IDENTITY_JAW, ...starts[i], tz: 0.4 * openSign });
    const base = jawTransformToMatrix(movableJaw === "upper" ? upperTx : lowerTx);
    const moved = matrixToJawTransform(offset.multiply(base));
    const result = await runContactSolver(
      baseUpper,
      baseLower,
      lowerBvh,
      movableJaw === "upper" ? moved : upperTx,
      movableJaw === "lower" ? moved : lowerTx,
      movableJaw,
      { ...solverOptions, preset: EXPLORE_PRESET, skipFinalLift: true }
    );
    explored.push(result);
    bestQuality = Math.max(bestQuality ?? 0, result.qualityScore);
  }

  const shortlist = distinctBest(explored, corners, 0.12, count);
  const preset = QUALITY_PRESETS[quality];
  const refined: BiteOptimizationResult[] = [];
  for (let i = 0; i < shortlist.length; i++) {
    if (options.shouldCancel?.()) return [];
    options.onProgress?.({ stage: "refine", done: i, total: shortlist.length, bestQuality });
    const result = await runContactSolver(
      baseUpper,
      baseLower,
      lowerBvh,
      shortlist[i].upperTx,
      shortlist[i].lowerTx,
      movableJaw,
      {
        ...solverOptions,
        skipSeat: true,
        preset: {
          ...preset,
          candidates: Math.min(preset.candidates, 1200),
          sigmaStart: 0.12,
          iterations: Math.ceil(preset.iterations * 0.4),
        },
      }
    );
    refined.push(result);
    bestQuality = Math.max(bestQuality ?? 0, result.qualityScore);
  }
  options.onProgress?.({
    stage: "refine",
    done: shortlist.length,
    total: shortlist.length,
    bestQuality,
  });

  const ranked = distinctBest(refined, corners, 0.08, count);
  if (ranked.length === 0) return [];
  const bestPose = relativePose(ranked[0].upperTx, ranked[0].lowerTx);
  const center = new THREE.Vector3();
  baseUpper.boundingBox!.getCenter(center);
  const bestCenter = center.clone().applyMatrix4(bestPose);
  const bestQuat = new THREE.Quaternion().setFromRotationMatrix(bestPose);
  return ranked.map((result, index) => {
    const pose = relativePose(result.upperTx, result.lowerTx);
    const offset = center.clone().applyMatrix4(pose).sub(bestCenter);
    const quat = new THREE.Quaternion().setFromRotationMatrix(pose);
    return {
      ...result,
      id: index + 1,
      offsetFromBestMm: { x: offset.x, y: offset.y, z: offset.z },
      rotationFromBestDeg: THREE.MathUtils.radToDeg(quat.angleTo(bestQuat)),
    };
  });
}

// ---------------------------------------------------------------------------
// Manual guidance

const ADJUSTMENT_AXES: AdjustmentAxis[] = ["tx", "ty", "tz", "rx", "ry", "rz"];

const AXIS_LABELS: Record<AdjustmentAxis, [string, string]> = {
  tx: ["Lateral X −", "Lateral X +"],
  ty: ["A-P Y −", "A-P Y +"],
  tz: ["Z −", "Z +"],
  rx: ["Pitch −", "Pitch +"],
  ry: ["Roll −", "Roll +"],
  rz: ["Yaw −", "Yaw +"],
};

function adjustmentLabel(axis: AdjustmentAxis, direction: 1 | -1, movable: "upper" | "lower") {
  if (axis === "tz") {
    const closes = movable === "upper" ? direction < 0 : direction > 0;
    return `${closes ? "Close" : "Open"} ${AXIS_LABELS.tz[direction < 0 ? 0 : 1]}`;
  }
  return AXIS_LABELS[axis][direction < 0 ? 0 : 1];
}

/** Area-weighted mean gap of the contact zone, with penetration counted 5× (mm). */
function contactZoneObjective(
  c: Candidates,
  upperMatrix: THREE.Matrix4,
  lowerCtx: SurfaceContext
): { objective: number; minD: number } {
  const p = new THREE.Vector3();
  const sample = newSignedSample();
  let sum = 0;
  let wSum = 0;
  let minD = Infinity;
  for (let k = 0; k < c.count; k++) {
    p.set(c.base[k * 3], c.base[k * 3 + 1], c.base[k * 3 + 2]).applyMatrix4(upperMatrix);
    signedDistance(lowerCtx, p, sample, 2.5);
    if (!sample.found) continue;
    const d = sample.signed;
    if (d < minD) minD = d;
    const phi = d < -PENETRATION_TOLERANCE_MM ? 5 * -d : Math.min(Math.max(d, 0), 1);
    sum += c.weight[k] * phi;
    wSum += c.weight[k];
  }
  return {
    objective: wSum > 0 ? sum / wSum : 0,
    minD: Number.isFinite(minD) ? minD : DIST_QUERY_CAP_MM,
  };
}

/** Rank single-axis jaw moves by how much they tighten the contact zone without interference. */
export function findContactAdjustments(
  baseUpper: THREE.BufferGeometry,
  baseLower: THREE.BufferGeometry,
  lowerBvh: MeshBVH,
  upperTx: JawTransform,
  lowerTx: JawTransform,
  movableJaw: "upper" | "lower",
  options: {
    translateStepMm?: number;
    rotateStepDeg?: number;
    count?: number;
    exclusionRegions?: ExclusionRegion[];
    priorityRegions?: PriorityRegion[];
    maxVertices?: number;
    partial?: boolean;
  } = {}
): ContactAdjustment[] {
  const count = options.count ?? MAX_CONTACT_ADJUSTMENTS;
  const translateStep = options.translateStepMm ?? 0.1;
  const rotateStep = options.rotateStepDeg ?? 0.5;
  const budget = options.maxVertices ?? 1200;
  const upperMatrix = jawTransformToMatrix(upperTx);
  const lowerMatrix = jawTransformToMatrix(lowerTx);

  const candidates = gatherCandidates(baseUpper, upperMatrix, makeSurfaceContext(baseLower, lowerBvh, lowerMatrix), {
    poolBudget: budget * 8,
    maxDistMm: 2.0,
    budget,
    exclusions: options.exclusionRegions,
    priorities: options.priorityRegions,
    partial: Boolean(options.partial),
  });
  if (candidates.count < 6) return [];

  const current = contactZoneObjective(candidates, upperMatrix, makeSurfaceContext(baseLower, lowerBvh, lowerMatrix));
  const ranked: Omit<ContactAdjustment, "id" | "rank">[] = [];

  for (const axis of ADJUSTMENT_AXES) {
    const step = axis.startsWith("r") ? rotateStep : translateStep;
    let best: Omit<ContactAdjustment, "id" | "rank"> | null = null;
    for (const direction of [-1, 1] as const) {
      const base = movableJaw === "upper" ? upperTx : lowerTx;
      const moved = { ...base, [axis]: base[axis] + direction * step };
      const u = movableJaw === "upper" ? jawTransformToMatrix(moved) : upperMatrix;
      const l = movableJaw === "lower" ? jawTransformToMatrix(moved) : lowerMatrix;
      const probe = contactZoneObjective(candidates, u, makeSurfaceContext(baseLower, lowerBvh, l));
      const improvement = current.objective - probe.objective;
      if (improvement > 1e-5 && (!best || improvement > best.improvementMm)) {
        best = {
          label: adjustmentLabel(axis, direction, movableJaw),
          axis,
          direction,
          step,
          improvementMm: improvement,
          currentMinGap: current.minD,
          projectedMinGap: probe.minD,
        };
      }
    }
    if (best) ranked.push(best);
  }

  return ranked
    .sort((a, b) => b.improvementMm - a.improvementMm)
    .slice(0, count)
    .map((item, index) => ({ ...item, id: index + 1, rank: index + 1 }));
}
